/**
 * Tour Management Hook
 * Handles first-visit detection, tour state persistence, and element visibility filtering
 */

import { useState, useEffect, useCallback } from 'react'
import { driver, DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import {
  employeeJourneyDemoSteps,
  managerJourneyDemoSteps,
  conductReviewTourSteps,
  setActiveDriver,
  DemoStep,
} from '@/utils/tour'

type TourType = 'employee-journey' | 'manager-journey' | 'conduct-review'

const TOUR_STORAGE_KEY = 'eduvos_tours_seen'

interface TourState {
  [key: string]: boolean
}

/**
 * Get tour completion state from localStorage
 */
const getTourState = (): TourState => {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

/**
 * Save tour completion state to localStorage
 */
const setTourSeen = (tourType: TourType): void => {
  try {
    const state = getTourState()
    state[tourType] = true
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(state))
  } catch {
    console.warn('Failed to save tour state')
  }
}

/**
 * Check if user has seen a specific tour
 */
const hasSeen = (tourType: TourType): boolean => {
  return getTourState()[tourType] === true
}

/**
 * Hook for managing interactive tours
 */
export function useTour(tourType: TourType) {
  const [isActive, setIsActive] = useState(false)
  // Load tour state synchronously to avoid race condition with auto-start
  const [hasSeenTour, setHasSeenTour] = useState(() => hasSeen(tourType))

  // Update state if tour type changes (rare but possible)
  useEffect(() => {
    setHasSeenTour(hasSeen(tourType))
  }, [tourType])

  // Get the right tour steps
  const getSteps = useCallback((): DemoStep[] => {
    switch (tourType) {
      case 'employee-journey':
        return employeeJourneyDemoSteps
      case 'manager-journey':
        return managerJourneyDemoSteps
      case 'conduct-review':
        return conductReviewTourSteps
      default:
        return []
    }
  }, [tourType])

  // Start the tour
  const startTour = useCallback(() => {
    setIsActive(true)
    const steps = getSteps()

    // --- Pass 1: Conditional filtering for employee objectives flow ---
    // Check once at start to determine which path to take
    const hasEmptyState = document.querySelector('[data-tour="empty-state"]') !== null
    const hasKpaView = document.querySelector('[data-tour="kpa-view"]') !== null

    // Elements for new users (no objectives) - shown in empty state flow
    const emptyStateSelectors = [
      '[data-tour="empty-state"]',
      '[data-tour="configure-objectives"]',
      '#tour-add-new-tab',
      '#tour-objective-options',
      '[data-tour="return-dashboard"]'
    ]
    // Elements for users with objectives - shown in KPA view flow
    const kpaViewSelectors = [
      '[data-tour="kpa-view"]'
    ]

    const conditionalFiltered = steps.filter(step => {
      if (step.element === 'body') return true

      // If element is in empty state flow, only include if user has empty state
      if (emptyStateSelectors.includes(step.element)) {
        return hasEmptyState
      }
      // If element is in KPA view flow, only include if user has KPA view
      if (kpaViewSelectors.includes(step.element)) {
        return hasKpaView
      }
      // Keep all other steps (tabs, header, buttons)
      return true
    })

    // --- Pass 2: Filter out steps targeting elements that don't exist or are hidden ---
    // This handles: removed elements, legacy year hidden tabs, mobile layout (desktop tabs hidden)
    // Note: Steps already handled by Pass 1 (conditional flow) are exempt — they appear
    // dynamically during the tour (e.g., objective config dialog elements)
    const dynamicSelectors = [...emptyStateSelectors, ...kpaViewSelectors]
    const visibleSteps = conditionalFiltered.filter(step => {
      if (step.element === 'body') return true
      // Skip visibility check for steps managed by Pass 1 conditional logic
      if (dynamicSelectors.includes(step.element)) return true
      const el = document.querySelector(step.element) as HTMLElement
      if (!el) return false
      // Check visibility — offsetWidth/Height are 0 for hidden elements (display:none, hidden class)
      return el.offsetWidth > 0 || el.offsetHeight > 0
    })

    // Convert to driver.js format
    const driverSteps: DriveStep[] = visibleSteps.map(step => ({
      element: step.element,
      popover: {
        title: step.popover.title,
        description: step.popover.description,
        side: step.popover.side || 'bottom',
        align: step.popover.align || 'start',
      },
      onHighlighted: step.onHighlighted,
      onDeselected: step.onDeselected,
    }))

    // Initialize driver.js
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: driverSteps,
      onDestroyed: () => {
        setIsActive(false)
        setTourSeen(tourType)
        setHasSeenTour(true)
        setActiveDriver(null)
      },
      popoverClass: 'eduvos-tour-popover',
      overlayColor: 'black',
      overlayOpacity: 0.75,
      stagePadding: 10,
      stageRadius: 8,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Previous',
      doneBtnText: 'Finish Tour ✓',
    })

    // Store the driver instance so tour helpers can call refresh()
    setActiveDriver(driverObj)

    // Start the tour
    driverObj.drive()
  }, [tourType, getSteps])

  // Auto-start tour on first visit
  const autoStartTour = useCallback(() => {
    if (!hasSeenTour) {
      // Small delay to ensure page is fully rendered
      const timer = setTimeout(() => {
        startTour()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [hasSeenTour, startTour])

  // Reset tour (for testing or "show tour again" functionality)
  const resetTour = useCallback(() => {
    try {
      const state = getTourState()
      delete state[tourType]
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(state))
      setHasSeenTour(false)
    } catch {
      console.warn('Failed to reset tour state')
    }
  }, [tourType])

  return {
    isActive,
    hasSeenTour,
    startTour,
    autoStartTour,
    resetTour,
  }
}

/**
 * CSS styles for the tour popover
 * These are injected when the tour starts
 */
export const tourStyles = `
  .eduvos-tour-popover {
    background: linear-gradient(135deg, #15305D 0%, #29ABE2 100%);
    color: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  }

  .eduvos-tour-popover .driver-popover-title {
    color: white;
    font-weight: 600;
    font-size: 1.1rem;
    margin-bottom: 8px;
  }

  .eduvos-tour-popover .driver-popover-description {
    color: rgba(255, 255, 255, 0.95);
    font-size: 0.95rem;
    line-height: 1.6;
  }

  .eduvos-tour-popover .driver-popover-progress-text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.85rem;
  }

  .eduvos-tour-popover button {
    background: rgba(255, 255, 255, 0.25);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.4);
    font-weight: 500;
    transition: all 0.2s ease;
    text-shadow: none;
  }

  .eduvos-tour-popover button:hover {
    background: rgba(255, 255, 255, 0.35);
    transform: translateY(-1px);
  }

  .eduvos-tour-popover .driver-popover-next-btn {
    background: linear-gradient(135deg, #29ABE2 0%, #1E88C7 100%);
    border: none;
  }

  .eduvos-tour-popover .driver-popover-next-btn:hover {
    background: linear-gradient(135deg, #1E88C7 0%, #29ABE2 100%);
  }

  .driver-active .driver-highlighted-element {
    outline: 3px solid #29ABE2;
    outline-offset: 4px;
  }

  /* Ensure driver.js overlay is above all UI elements */
  .driver-overlay {
    z-index: 99999 !important;
  }

  .driver-popover {
    z-index: 100000 !important;
  }

  .driver-highlighted-element {
    z-index: 99999 !important;
    position: relative !important;
  }
`
